import { createContext, FC, PropsWithChildren, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useToggle } from 'react-use';
import { isMobile } from 'react-device-detect';
import Sheet from 'react-modal-sheet';
import { Divider } from 'common/divider';
import { RiInstallFill } from 'react-icons/ri';
import { saveToLocalStorage } from 'global/utils/storage';
import { IS_PWA_INSTALLATION_CANCELLED } from 'config/constant';

const context = createContext<any>({
  isInstalled: () => false,
  supported: () => false,
});

export const usePWAInstall = () => useContext(context);

export const PWAInstallProvider: FC<PropsWithChildren<any>> = ({ children }) => {
  const awaitingPromiseRef = useRef<any>();
  const deferredPrompt = useRef(null);
  const [isDialogShow, setIsDialogShow] = useToggle(false);
  const [contextValue, setContextValue] = useState({
    isInstalled: false,
    supported: false,
  });

  const handleBeforeInstallPromptEvent = useCallback((event: any) => {
    event.preventDefault();
    deferredPrompt.current = event;
    setContextValue({
      isInstalled: isInstalled(),
      supported: supported(),
    });
  }, []);

  const openDialog = useCallback(() => {
    setIsDialogShow(true);
    return new Promise((resolve, reject) => {
      awaitingPromiseRef.current = { reject, resolve };
    });
  }, [setIsDialogShow]);

  useEffect(() => {
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPromptEvent);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPromptEvent);
    };
  }, [handleBeforeInstallPromptEvent]);

  useEffect(() => {
    if (contextValue.supported && !contextValue.isInstalled) {
      openDialog();
    }
  }, [contextValue, openDialog]);

  const isInstalled = useCallback(
    () =>
      // @ts-ignore
      window.navigator?.standalone === true || window.matchMedia('(display-mode: standalone)').matches,
    [],
  );

  const supported = useCallback(() => deferredPrompt.current != null && isMobile, []);

  const handleClose = useCallback(() => {
    saveToLocalStorage(IS_PWA_INSTALLATION_CANCELLED, 'true');
    setIsDialogShow(false);
    if (awaitingPromiseRef.current) {
      awaitingPromiseRef.current?.reject();
    }
  }, []);

  const handleInstall = useCallback(() => {
    setIsDialogShow(null);
    if (deferredPrompt.current != null) {
      return (
        deferredPrompt.current
          // @ts-ignore
          .prompt()
          // @ts-ignore
          .then(() => deferredPrompt.current.userChoice)
          .then((choiceResult: any) => {
            if (choiceResult.outcome === 'accepted') {
              if (awaitingPromiseRef.current) {
                awaitingPromiseRef.current?.resolve();
              }
            } else if (awaitingPromiseRef.current) {
              awaitingPromiseRef.current?.reject();
            }
          })
          .catch(() => {
            if (awaitingPromiseRef.current) {
              awaitingPromiseRef.current?.resolve();
            }
          })
      );
    }

    if (awaitingPromiseRef.current) {
      awaitingPromiseRef.current?.resolve();
    }
  }, []);

  return (
    <>
      <context.Provider value={contextValue}>{children}</context.Provider>

      <Sheet isOpen={isDialogShow} snapPoints={[260]} onClose={handleClose}>
        <Sheet.Container>
          <Sheet.Header>
            <div className="flex justify-between items-center p-4 border-b-2">
              <span>Billetera Vehicular App</span>
              <button className="font-semibold" type="button" onClick={handleClose}>
                Cancelar
              </button>
            </div>
          </Sheet.Header>
          <Sheet.Content>
            <div className="px-4 pb-4 flex flex-col">
              <p className="text-center py-4">
                Esta página web tiene funcionalidades de una aplicación, adiciónala para utilizarla en pantalla
                completa.
              </p>
              <Divider />
              <button className="py-4 flex gap-4 items-center" type="button" onClick={handleInstall}>
                <RiInstallFill size={30} />
                <span>Instalar aplicación localmente.</span>
              </button>
            </div>
          </Sheet.Content>
        </Sheet.Container>

        <Sheet.Backdrop />
      </Sheet>
    </>
  );
};
